import { get, isArray } from 'lodash';

import { actions } from 'Data/actions';
import config from '~/config';
import telehealthRequest from 'Services/telehealthRequest';

export const MeetingActionTypes = {
  MEETING_CREATE: 'MEETING_CREATE',
  MEETING_END: 'MEETING_END',
  MEETING_GET_INFO: 'MEETING_GET_INFO',
  MEETING_JOIN: 'MEETING_JOIN',
  INCOMING_PATIENT_CONTACT_PARAMETERS: 'INCOMING_PATIENT_CONTACT_PARAMETERS',
  MEETING_STORE_PATIENT_CODE: 'MEETING_STORE_PATIENT_CODE',
  MEETING_PUT_PATIENT_DETAILS: 'MEETING_PUT_PATIENT_DETAILS',
};

const meetingCreate = (patientMobileOrEmail, payloadKey) => {
  if (!patientMobileOrEmail) throw new Error('[meetingCreate] A patient mobile or email is required');
  if (!payloadKey) throw new Error('[meetingCreate] An invite payloadKey method is required');

  const url = '/meetings';
  const payload = {
    [payloadKey]: patientMobileOrEmail,
  };
  return (dispatch) => {
    dispatch(actions.requestStart(MeetingActionTypes.MEETING_CREATE));
    return telehealthRequest({ url, method: 'POST', data: payload })
      .then((response) => dispatch(actions.requestReceive(MeetingActionTypes.MEETING_CREATE, response)))
      .catch((error) => {
        if (get(error, ['response', 'status']) === 400) {
          dispatch(actions.snackbarCreate('Could not create meeting', { severity: 'error' }));
        } else if (get(error, ['response', 'status']) === 403) {
          dispatch(actions.snackbarCreate('You do not have access to create meetings', { severity: 'error' }));
        } else {
          dispatch(actions.snackbarCreate('Something went wrong', { severity: 'error' }));
        }
        return dispatch(actions.requestError(MeetingActionTypes.MEETING_CREATE, error));
      });
  };
};

const meetingEnd = (consultGuid) => {
  if (!consultGuid) throw new Error('[meetingEnd] A consult guid is required');

  const url = `/meetings/${consultGuid}/end`;
  return (dispatch) => {
    dispatch(actions.requestStart(MeetingActionTypes.MEETING_END));
    return telehealthRequest({ url, method: 'PUT' })
      .then(() => dispatch(actions.requestReceive(MeetingActionTypes.MEETING_END)))
      .catch((error) => {
        if (get(error, ['response', 'status']) === 400) {
          dispatch(actions.snackbarCreate('Could not get consult state', { severity: 'error' }));
        } else if (get(error, ['response', 'status']) === 403) {
          dispatch(actions.snackbarCreate('You do not have access to end meetings', { severity: 'error' }));
        } else {
          dispatch(actions.snackbarCreate('Something went wrong', { severity: 'error' }));
        }
        return dispatch(actions.requestError(MeetingActionTypes.MEETING_END, error));
      });
  };
};

const meetingGetState = (consultGuid) => {
  if (!consultGuid) throw new Error('[meetingGetState] A consult guid is required');

  const url = `/meetings/${consultGuid}/state`;
  return (dispatch) => {
    dispatch(actions.requestStart(MeetingActionTypes.MEETING_GET_INFO));
    return telehealthRequest({ url })
      .then((response) => dispatch(actions.requestReceive(MeetingActionTypes.MEETING_GET_INFO, response)))
      .catch((error) => {
        if (get(error, ['response', 'status']) === 400) {
          dispatch(actions.snackbarCreate('Could not get consult state', { severity: 'error' }));
        } else {
          dispatch(actions.snackbarCreate('Something went wrong', { severity: 'error' }));
        }
        return dispatch(actions.requestError(MeetingActionTypes.MEETING_GET_INFO, error));
      });
  };
};

const meetingJoin = (patientName, meetingId, attendeeGuid) => {
  if (!meetingId) throw new Error('[meetingJoin] A consult guid is required');
  if (!attendeeGuid) throw new Error('[meetingJoin] An attendee guid is required');
  if (!patientName) throw new Error('[meetingJoin] A patient name is required');

  const url = `/meetings/${meetingId}/attendees/${attendeeGuid}/join`;
  const payload = {
    patientName,
  };
  return (dispatch) => {
    dispatch(actions.requestStart(MeetingActionTypes.MEETING_JOIN));
    return telehealthRequest({ url, method: 'PUT', data: payload })
      .then((response) => {
        dispatch(actions.requestReceive(MeetingActionTypes.MEETING_JOIN, response));
        return true;
      })
      .catch((error) => {
        if (get(error, ['response', 'status']) === 400) {
          dispatch(actions.snackbarCreate('Connection to consult failed', { severity: 'error' }));
        } else {
          dispatch(actions.snackbarCreate('Something went wrong', { severity: 'error' }));
        }
        dispatch(actions.requestError(MeetingActionTypes.MEETING_JOIN, error));
        return false;
      });
  };
};

const meetingEndAndPatientToComplete = () => (dispatch, getState) => {
  const currentState = getState();
  const consultGuid = get(currentState, ['Location', 'payload', 'consult_guid']);
  const attendeeGuid = get(currentState, ['Location', 'payload', 'attendee_guid']);
  dispatch(actions.requestReceive(MeetingActionTypes.MEETING_END));
  return dispatch({
    type: 'PATIENT_MEETING_COMPLETE',
    payload: {
      consult_guid: consultGuid,
      attendee_guid: attendeeGuid,
    },
  });
};

const meetingPutPatientCode = (code, dataTransferFailed) => (dispatch) => {
  const showError = (dataTransferFailed === 'true');
  return dispatch({
    type: MeetingActionTypes.MEETING_STORE_PATIENT_CODE,
    payload: {
      code,
      showError,
    },
  });
};

const meetingPutPatientDetails = (code) => (dispatch) => {
  if (!code) {
    throw new Error('[meetingPutPatientDetails] A code is required');
  }
  const url = config.DATA_BRIDGE_RETRIEVE_URL;
  const payload = { code };
  dispatch(actions.requestStart(MeetingActionTypes.MEETING_PUT_PATIENT_DETAILS));
  return telehealthRequest({ url, method: 'PUT', data: payload })
    .then(({ data: { mobile, email }}) => {
      dispatch(actions.requestReceive(MeetingActionTypes.MEETING_PUT_PATIENT_DETAILS, {
        email,
        mobile,
      }));
    })
    .catch((error) => {
      dispatch(actions.requestError(MeetingActionTypes.MEETING_CREATE, error));
      dispatch(actions.requestReceive(MeetingActionTypes.MEETING_STORE_PATIENT_CODE, { showError: true }));
    });
};

const recordUnsupportedBrowser = (meetingId, attendeeId, browserDetails) => () => {
  if (!meetingId || !attendeeId || !browserDetails) return null;

  const url = '/event/generic';
  const payload = {
    eventName: 'unsupported-browser',
    meetingId,
    attendeeId,
    ...browserDetails,
  };
  return telehealthRequest({
    url,
    method: 'POST',
    data: payload,
  }).catch((error) => {
    console.error('error sending unsupported browser event: ', error);
  });
};

export const MeetingActions = {
  meetingCreate,
  meetingEnd,
  meetingGetState,
  meetingJoin,
  meetingEndAndPatientToComplete,
  meetingPutPatientCode,
  meetingPutPatientDetails,
  recordUnsupportedBrowser,
};
